import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { BrandKitLogoState, OverlayPosition, OverlaySize } from 'js/types';

const initialState: BrandKitLogoState = {
  lastSynced: null,
  assetId: null,
  contentType: null,
  filename: null,
  position: 'bottom-right',
  opacity: 80,
  size: 'medium',
  showOverlay: true,
  uploadStatus: 'idle',
  uploadError: null
};

type PersistedLogoState = Omit<BrandKitLogoState, 'uploadStatus' | 'uploadError' | 'lastSynced'>;

const brandKitLogoSlice = createSlice({
  name: 'brandKitLogo',
  initialState,
  reducers: {
    loadLogoSuccess: (state, action: PayloadAction<PersistedLogoState>) => {
      state.assetId = action.payload.assetId;
      state.contentType = action.payload.contentType;
      state.filename = action.payload.filename;
      state.position = action.payload.position;
      state.opacity = action.payload.opacity;
      state.size = action.payload.size;
      state.showOverlay = action.payload.showOverlay;
    },
    // file is non-serializable; only uploadStatus is stored — the saga reads the file from the action
    uploadLogoRequested: (state, _action: PayloadAction<{ file: File; filename: string }>) => {
      state.uploadStatus = 'uploading';
      state.uploadError = null;
    },
    uploadLogoSuccess: (state, action: PayloadAction<{ assetId: number; contentType: string; filename: string }>) => {
      state.assetId = action.payload.assetId;
      state.contentType = action.payload.contentType;
      state.filename = action.payload.filename;
      state.uploadStatus = 'success';
      state.uploadError = null;
    },
    uploadLogoFailure: (state, action: PayloadAction<string>) => {
      state.uploadStatus = 'error';
      state.uploadError = action.payload;
    },
    deleteLogoRequested: state => {
      state.uploadStatus = 'uploading';
      state.uploadError = null;
    },
    deleteLogoSuccess: state => {
      state.assetId = null;
      state.contentType = null;
      state.filename = null;
      state.uploadStatus = 'success';
    },
    updateOverlaySettings: (
      state,
      action: PayloadAction<{ position?: OverlayPosition; opacity?: number; size?: OverlaySize; showOverlay?: boolean }>
    ) => {
      if (action.payload.position !== undefined) state.position = action.payload.position;
      if (action.payload.opacity !== undefined) state.opacity = action.payload.opacity;
      if (action.payload.size !== undefined) state.size = action.payload.size;
      if (action.payload.showOverlay !== undefined) state.showOverlay = action.payload.showOverlay;
    },
    setLastSynced: (state, action: PayloadAction<number>) => {
      state.lastSynced = action.payload;
    }
  }
});

export const {
  loadLogoSuccess,
  uploadLogoRequested,
  uploadLogoSuccess,
  uploadLogoFailure,
  deleteLogoRequested,
  deleteLogoSuccess,
  updateOverlaySettings,
  setLastSynced
} = brandKitLogoSlice.actions;

export default brandKitLogoSlice.reducer;
